import { styled, Box, Grid, Typography } from "@mui/material"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useSsoLoginMutation, useUserLoginMutation } from "@client-hub/store/api/crs/react"
import ROUTES from "@client-hub/shared/constants/routes"
import Link from "@client-hub/shared/react/components/Link"
import loginBackground from "assets/login_background.png"
import LoginBase from "components/LoginBase"
import { CRS_LOGIN_MUTATION_CACHE_KEY } from "constants/auth"
import { useTranslation } from "react-i18next"
import useAppConfiguration from "@client-hub/shared/react/hooks/useAppConfiguration"
import { useAnalyticsTrackEvent } from "@client-hub/shared/react/components/AnalyticsProvider"
import { ACTION, APP_NAME } from "@client-hub/shared/constants/analytics"
import { routeToEventRoute } from "@client-hub/shared/utils/event"
import { AlertMUI, Button } from "@icanbwell/ui-toolkit"
import { CredentialsData } from "components/Form/Credentials"
import { selectRememberMeEmail } from "@client-hub/store/slices/metadata/selectors"
import { useSelector } from "react-redux"
import { useGetClientBrandingQuery } from "@client-hub/store/api/gcs/augmented"
import isHtmlStringEmpty from "@client-hub/shared/utils/isHtmlStringEmpty"
import { REGEX_PATTERNS } from "@client-hub/shared/constants"
import Okta from 'assets/okta.png'

const StyledLoginBase = styled(LoginBase)({
  background: `linear-gradient(0deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${loginBackground}) no-repeat fixed center`,
  backgroundSize: "cover",
})
const LinkWrapper = styled(Link)(({ theme }) => ({
  textDecoration: "none",
  color: "white",
  fontSize: "20px",
  borderBottom: "none",
  "&:hover": {
    textDecoration: "underline",
  },
}))
const FaviconWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  transform: "translate(-72px, 2px)",
  paddingRight: "15px",
  borderRight: `1px solid ${theme.palette.grey[400]}`,
  display: "flex",
  alignItems: "center",
  "& img": {
    width: "30px",
  },
}))

const DisclaimerTextWrapper = styled(Typography)({
  background: "white",
  zIndex: 1,
  position: "fixed",
  bottom: "0",
  width: "100%",
  "& p": {
    margin: 0,
  },
})

const checkIfUsernameIsSSODomain = (domainsString: string | undefined, username: string) => {
  const usernameToLower = username.toLowerCase()
  if (!username || !(new RegExp(REGEX_PATTERNS.EMAIL_REGEX).test(usernameToLower)) || !domainsString) return false

  const userEmailDomain = usernameToLower.split('@')[1]
  const domains = domainsString.split(',')

  return domains.some((domain) => userEmailDomain === domain.trim())
}

const INTERNAL_SSO_SHORTCUT_MACRO = "i"

const Login = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { trackEvent } = useAnalyticsTrackEvent()
  const rememberMeUserEmail = useSelector(selectRememberMeEmail)
  const [isSSODomain, setisSSODomain] = useState(false)
  const [isInternalSSOEnabled, setIsInternalSSOEnabled] = useState(false)
  const [userEmail, setUserEmail] = useState(rememberMeUserEmail)
  const [ssoLogin, { data: { results: ssoLink } = { results: null }, isLoading: isSSOLoading, isError: isSSOError  }] = useSsoLoginMutation()
  const location = useLocation()
  const locationState = location.state as { from: Location } | null
  const [login, { error, isSuccess, data }] = useUserLoginMutation({ fixedCacheKey: CRS_LOGIN_MUTATION_CACHE_KEY })
  const { legalDocumentAcknowledgementText, tosDocLink, privacyPolicyDocLink, sso, disclaimerText, internalSSODomains } =
    useAppConfiguration()
  const { data: clientbrandingQueryData } = useGetClientBrandingQuery()
  const favicon = clientbrandingQueryData?.branding?.themeUrls?.favicon
  const trackTOSORPPEvent = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    const eventTarget = e.target as HTMLAnchorElement
    if (eventTarget) {
      const eventHref = eventTarget.getAttribute("href") || eventTarget.querySelector("a")?.getAttribute("href")
      if (eventHref) {
        let action: string | undefined
        if (eventHref === ROUTES.TERMS_OF_SERVICE) {
          action = ACTION.CLICKED_TERMS_SERVICE
        } else if (eventHref === ROUTES.PRIVACY_POLICY) {
          action = ACTION.CLICKED_PRIVACY_POLICY
        }
        if (action) {
          trackEvent({
            appName: APP_NAME,
            route: routeToEventRoute(ROUTES.LOGIN.BASE),
            action,
          })
        }
        navigate(eventHref)
      }
    }
  }

  const isUsingSSO = isSSODomain || isInternalSSOEnabled

  const onCredentialsFormChange = ({ username }: CredentialsData) => {
    setUserEmail(username)
    if (sso.requiredDomains) setisSSODomain(checkIfUsernameIsSSODomain(sso.requiredDomains, username))
    if (internalSSODomains) setIsInternalSSOEnabled(checkIfUsernameIsSSODomain(internalSSODomains, username))
  }

  useEffect(() => {
    if (ssoLink) {
      window.open(ssoLink, "_self")
    }
  }, [ssoLink])

  const loginUserWithSSO = () => {
    trackEvent({
      appName: APP_NAME,
      route: routeToEventRoute(ROUTES.LOGIN.BASE),
      action: ACTION.CLICKED_SSO,
    })
    ssoLogin({ username: userEmail })
  }

  const loginUserWithInternalSSO = () => {
    trackEvent({
      appName: APP_NAME,
      route: routeToEventRoute(ROUTES.LOGIN.BASE),
      action: ACTION.CLICKED_INTERNAL_SSO,
    })
    ssoLogin({ username: userEmail, config: { isInternalUser: true } })
  }

  useEffect(() => {
    if (isSuccess && data) {
      const { results, uid = "", two_factor_auth = 0, sms_status } = data
      // TODO: test this case
      // MFA enforced if no "results" field from server
      if (results) {
        navigate(locationState ? locationState.from.pathname : "/", { replace: true })
      } else {
        if (two_factor_auth) {
          const state = {
            uid,
            two_factor_auth,
            sms_status,
            isAllowedMfa: true,
            ...(locationState?.from && { from: locationState.from }),
          }
          navigate(ROUTES.LOGIN.MFA.BASE, { state })
        } else {
          const state = { uid, two_factor_auth, from: locationState?.from, isAllowedMfa: true }
          navigate(ROUTES.MFA.SETUP.BASE, { state })
        }
      }
    }
  }, [data, isSuccess, locationState, navigate, location.pathname])

  useEffect(() => {
    if (rememberMeUserEmail) {
      setisSSODomain(checkIfUsernameIsSSODomain(sso.requiredDomains, rememberMeUserEmail))
      setIsInternalSSOEnabled(checkIfUsernameIsSSODomain(internalSSODomains, rememberMeUserEmail))
    }
  }, [sso.requiredDomains, internalSSODomains])

  /**
   * trigger the internal SSO using a shortcut (cmd + o ; ctrl + o)
   */
  useEffect(() => {
    const eventListener = function (event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === INTERNAL_SSO_SHORTCUT_MACRO) {
        event.preventDefault()
        setIsInternalSSOEnabled(prev => !prev)

        /**
         * internal SSO auto log in
         */
        if(!isInternalSSOEnabled){
          loginUserWithInternalSSO()
        }
      }
    }

    if (document && internalSSODomains) {
      document.addEventListener("keydown", eventListener)
    }

    return () => document.removeEventListener("keydown", eventListener)
  }, [internalSSODomains])  

  useEffect(() => {
    if(isSSOError){
      setIsInternalSSOEnabled(false)
      setisSSODomain(false)
    }
  }, [isSSOError])

  return (
      <StyledLoginBase
        onSubmit={login}
        serverError={error}
        rememberMe={true}
        title={isUsingSSO ? t("routes.login.ssoTitle") : t("routes.login.title")  }
        sso={isUsingSSO}
        onChange={onCredentialsFormChange}
      >
        {isSSOError && <AlertMUI sx={{ mt: 3 }} severity="error">{t("api.crs.error.SSOError")}</AlertMUI>}

        <Box sx={{ display: "flex", justifyContent: "center", width: "100%", marginTop: "15px" }}>
          <Box width="20em">
            <Grid container sx={{ marginTop: "16px" }}>
              {!isUsingSSO && (
                <Grid item xs={12} sm={8}>
                  <LinkWrapper
                    to={ROUTES.FORGOT_PASSWORD}
                    onClick={() => {
                      trackEvent({
                        appName: APP_NAME,
                        route: routeToEventRoute(ROUTES.LOGIN.BASE),
                        action: ACTION.CLICKED_FORGOT_PASSWORD,
                      })
                    }}
                  >
                    {t("forms.credentials.forgotPassword")}
                  </LinkWrapper>
                </Grid>
              )}

              {/* internal SSO; OKTA */}
              {isInternalSSOEnabled && (
                <Box textAlign="center" sx={{ display: "flex", justifyContent: "center", width: "100%", mt: 3 }}>
                  <Button
                    width="100%"
                    text={t("forms.credentials.signInWithInternalSso")}
                    onClick={loginUserWithInternalSSO}
                    loading={isSSOLoading}
                    disabled={isSSOLoading}
                    sx={{
                       background: "white", color: "#2E3586", whiteSpace: "nowrap"
                    }}
                    startIcon={
                      <Box sx={{
                          height: '30px',
                          width: '30px',
                          border: '1px dashed white',
                          background: `url(${Okta})`,
                          backgroundSize: '30px 30px',
                          backgroundPosition: 'center',
                          mixBlendMode: 'multiply'
                        }} 
                      />
                    }
                  />
                </Box>
              )}

              {/* external SSO */}
              {sso.enabled && (
                <Grid container>
                  {!isSSODomain && (
                    <Typography
                      textAlign="center"
                      sx={{ width: "100%", color: "white", margin: "1.5rem  0", fontSize: "18px" }}
                    >
                      {t("routes.login.or")}
                    </Typography>
                  )}
                  <Box textAlign="center" sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                    <Button
                      width="100%"
                      sx={{ background: "white", color: "#2E3586", whiteSpace: "nowrap" }}
                      onClick={loginUserWithSSO}
                    >
                      {favicon && (
                        <FaviconWrapper>
                          <img src={favicon} alt="favicon" />
                        </FaviconWrapper>
                      )}
                      {sso.loginButtonText!}
                    </Button>
                  </Box>
                </Grid>
              )}

              {tosDocLink && privacyPolicyDocLink && (
                <Grid item xs={12}>
                  <Box
                    data-testid="legalDocumentAcknowledgementText"
                    textAlign="center"
                    color="white"
                    fontSize="0.98rem"
                    mt="1.5rem"
                    mx="10px"
                    sx={{ "& a": { color: "white", display: "inline-block" } }}
                    dangerouslySetInnerHTML={{
                      __html: legalDocumentAcknowledgementText,
                    }}
                    onClick={trackTOSORPPEvent}
                  ></Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>

        {!isHtmlStringEmpty(disclaimerText) && (
          <DisclaimerTextWrapper
            variant="body2"
            textAlign="center"
            fontWeight={700}
            sx={{
              padding: [2, 3],
            }}
            dangerouslySetInnerHTML={{
              __html: disclaimerText,
            }}
          />
        )}
      </StyledLoginBase>
  )
}

export default Login
